import { Checkbox, FormControlLabel, Grid } from "@material-ui/core";
import { getKycImage, hasOnFileImage } from "../logic/attachments";
import {
  freshIdReason,
  getManilaToday,
} from "../logic/idValidation";
import { rv } from "../tokens";
import DateField from "../ui/fields/DateField";
import { AlertCircleIcon } from "../ui/icons";
import UploadTile from "../ui/UploadTile";
import { StepViewProps } from "./types";

type Props = Pick<StepViewProps, "state" | "handlers">;

// Government ID documents: a "why" notice, front/back upload tiles, and the ID
// expiration controls.
export default function IdDocuments({ state, handlers }: Props) {
  const { draft, originalKyc, uploads, errors } = state;
  const ids = draft.kyc.ids || {};
  const hasExpiration = ids.hasExpiration !== false;

  const originalIds = originalKyc.ids || {};

  const reason = freshIdReason({
    original: originalIds,
    draft: ids,
    hasExistingFront: hasOnFileImage(originalKyc, "idPhotoFront"),
    hasExistingBack: hasOnFileImage(originalKyc, "idPhotoBack"),
  });
  const required = reason !== "";

  return (
    <>
      {required && (
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            background: rv.pendingBg,
            border: `1px solid ${rv.pendingBorder}`,
            borderRadius: 10,
            padding: "12px 14px",
            marginTop: 4,
            color: rv.pendingFg,
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <span style={{ flexShrink: 0, marginTop: 1 }}>
            <AlertCircleIcon size={16} />
          </span>
          <span>
            <strong style={{ fontWeight: 600 }}>{reason}</strong> To keep your
            account verified, confirm your ID expiration details and upload
            current photos of the front and back of your ID.
          </span>
        </div>
      )}

      <Grid container spacing={2} style={{ marginTop: 4 }}>
        <Grid item xs={12} sm={6}>
          <UploadTile
            label="Front of ID"
            required={required}
            entry={uploads.idPhotoFront}
            onFileImage={getKycImage(originalKyc, "idPhotoFront")}
            error={errors.idPhotoFront}
            onFile={(file) => handlers.uploadFile("idPhotoFront", file)}
            onRemove={() => handlers.removeUpload("idPhotoFront")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <UploadTile
            label="Back of ID"
            required={required}
            entry={uploads.idPhotoBack}
            onFileImage={getKycImage(originalKyc, "idPhotoBack")}
            error={errors.idPhotoBack}
            onFile={(file) => handlers.uploadFile("idPhotoBack", file)}
            onRemove={() => handlers.removeUpload("idPhotoBack")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 12 }}>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!hasExpiration}
                color="primary"
                onChange={(event) => {
                  const nextHasExpiration = !event.target.checked;
                  handlers.setKycField("ids.hasExpiration", nextHasExpiration);
                  if (!nextHasExpiration) {
                    handlers.setKycField("ids.idValidity", "");
                  }
                }}
              />
            }
            label="My ID has no validity"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateField
            label="ID expiration"
            required={hasExpiration}
            disabled={!hasExpiration}
            min={getManilaToday()}
            value={ids.idValidity}
            error={errors["ids.idValidity"]}
            onChange={(v) => handlers.setKycField("ids.idValidity", v)}
          />
        </Grid>
      </Grid>
    </>
  );
}
